import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Tooltip } from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useSnackbar } from 'notistack';
import { useRetention } from '../RetentionContext';
import { GO_LIVE_STAGE, stageByNo } from '../constants/stages';
import { missingRequired } from '../constants/stageFields';
import { advanceStage } from '../../../redux/features/retentionSlice';

// Stage transition buttons for a StageCard (POST /retention/:id/advance-stage, RETENTION_WORKFLOW.md §6.3):
// the current stage gets "Mark complete" ("Confirm admission" at Go-live), which also starts and opens the next
// stage; "Start stage" on the next one stays for students whose stage was completed without that (older data).
// The server enforces the order, the Admission Confirmed lock and the fee-details rule.
export default function StageActions({ stageNo, progress }) {
  const { courseEnrollmentId, currentStage, refresh, headerReady, student } = useRetention();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const profile =useSelector((state) => state.retention.profile.data?.profile);
  const { enqueueSnackbar } = useSnackbar();
  const [busy, setBusy] = useState(false);

  if (!headerReady) return null;
  const canComplete = stageNo === currentStage && progress?.stage_status !== 2;
  const canStart = stageNo === currentStage + 1;
  if (!canComplete && !canStart) return null;

  const run = async (stageStatus) => {
    setBusy(true);
    const res = await dispatch(advanceStage({ courseEnrollmentId, stage_no: stageNo, stage_status: stageStatus }));
    setBusy(false);
    const ok = res.payload?.status === 'success';
    enqueueSnackbar(res.payload?.message || (ok ? 'Updated' : 'Could not update the stage'), { variant: ok ? 'success' : 'error' });
    if (!ok) return;
    refresh();
    // completing a stage also starts the next one (server side) — open it, switching tab when it is in another phase
    const nextStage = stageStatus === 2 ? stageByNo(stageNo + 1) : null;
    if (nextStage) navigate(`/student-journey/${courseEnrollmentId}/${nextStage.tab}?stage=${nextStage.no}`);
  };

  if (canComplete) {
    const goLive = stageNo === GO_LIVE_STAGE;
    // stages 1–3: same gate as the API's assertStageDone (saved values only; stage 1 also needs Mark verified)
    let blocker = '';
    if ([1, 2, 3].includes(stageNo) && profile) {
      const missing = missingRequired(stageNo, { ...(student || {}), ...profile });
      if (missing.length) blocker = `Save these first: ${missing.join(', ')}`;
      else if (stageNo === 1 && !profile.basics_verified_at) blocker = 'Mark the basics verified first';
    }
    return (
      <Tooltip title={blocker}>
        <span>
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={busy || Boolean(blocker)}
            onClick={() => run(2)}
            startIcon={<Icon icon={goLive ? 'solar:verified-check-bold' : 'solar:check-circle-bold'} width={16} />}
          >
            {goLive ? 'Confirm admission' : 'Mark complete'}
          </Button>
        </span>
      </Tooltip>
    );
  }
  return (
    <Button size="small" variant="contained" disabled={busy} onClick={() => run(1)} startIcon={<Icon icon="solar:play-bold" width={14} />}>
      Start stage
    </Button>
  );
}
